import React, { useEffect } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  IconButton,
  useTheme,
} from '@mui/material';
import { CheckCircle, Warning, Error, Info, Close } from '@mui/icons-material';
import { myNotificationStyles } from './MyNotification.styles';

function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const MyNotification = ({
  open = false,
  type = 'info',
  title,
  message,
  autoHide = true,
  autoHideDuration = 6000,
  onClose,
  position = 'top-right',
  index = 0,
  noSnackbar = false,
  ...props
}) => {
  const theme = useTheme();
  useEffect(() => {
    if (open && autoHide && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [open, autoHide, autoHideDuration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle />;
      case 'warning':
        return <Warning />;
      case 'error':
        return <Error />;
      case 'info':
      default:
        return <Info />;
    }
  };

  const getSeverity = () => {
    switch (type) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'info':
      default:
        return 'info';
    }
  };

  const getAnchorOrigin = () => {
    switch (position) {
      case 'bottom-right':
        return { vertical: 'bottom', horizontal: 'right' };
      case 'top-right':
      default:
        return { vertical: 'top', horizontal: 'right' };
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    if (onClose) {
      onClose();
    }
  };

  const alertStyles = {
    ...myNotificationStyles.alert,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.24)'
        : '0 8px 32px rgba(0, 0, 0, 0.12)',
    borderColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.08)',
  };

  if (noSnackbar) {
    return (
      <Alert
        onClose={handleClose}
        severity={getSeverity()}
        icon={getIcon()}
        sx={alertStyles}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
            sx={myNotificationStyles.closeButton}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        {title && (
          <AlertTitle sx={myNotificationStyles.alertTitle}>{title}</AlertTitle>
        )}
        {message && <div sx={myNotificationStyles.alertMessage}>{message}</div>}
      </Alert>
    );
  }

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={getAnchorOrigin()}
      TransitionComponent={SlideTransition}
      sx={{
        ...myNotificationStyles.snackbar,
        top: position?.includes('top') ? `${24 + (index || 0) * 80}px` : undefined,
        bottom: position?.includes('bottom') ? `${24 + (index || 0) * 80}px` : undefined,
        right: 24,
      }}
      {...props}
    >
      <Alert
        onClose={handleClose}
        severity={getSeverity()}
        icon={getIcon()}
        sx={alertStyles}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
            sx={myNotificationStyles.closeButton}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        {title && (
          <AlertTitle sx={myNotificationStyles.alertTitle}>{title}</AlertTitle>
        )}
        {message && <div sx={myNotificationStyles.alertMessage}>{message}</div>}
      </Alert>
    </Snackbar>
  );
};

export default MyNotification;
